import cn from 'classnames';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resizeObserver';

interface AutoScrollingContainerProps {
  children: React.ReactNode;
  speed?: number;
  className?: string;
}

interface DragState {
  isDragging: boolean;
  startX: number;
  scrollLeft: number;
  startTime: number;
}

const AutoScrollingContainer: React.FC<AutoScrollingContainerProps> = ({
  children,
  speed = 16,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMovedRef = useRef(false);
  const autoScrollPositionRef = useRef(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [dragState, setDragState] = useState<DragState | null>(null);

  const debouncedWheelHandler = useCallback(
    debounce(() => setIsAutoScrollPaused(true), 150),
    [],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
          setIsAutoScrollPaused(!entry.isIntersecting);
        });
      },
      { threshold: 0.1 },
    );

    observerRef.current.observe(container);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    resizeObserverRef.current = new ResizeObserver(() => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      autoScrollPositionRef.current = Math.min(
        autoScrollPositionRef.current,
        maxScroll,
      );
      container.scrollLeft = autoScrollPositionRef.current;
    });

    resizeObserverRef.current.observe(container);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;

    hasMovedRef.current = false;
    setDragState({
      isDragging: true,
      startX: e.pageX - containerRef.current.offsetLeft,
      scrollLeft: containerRef.current.scrollLeft,
      startTime: Date.now(),
    });
    setIsAutoScrollPaused(true);
  }, []);

  const handleDragMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragState || !containerRef.current) return;

      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = x - dragState.startX;

      if (Math.abs(walk) > 5) {
        hasMovedRef.current = true;
      }

      containerRef.current.scrollLeft = dragState.scrollLeft - walk;
      autoScrollPositionRef.current = dragState.scrollLeft - walk;
    },
    [dragState],
  );

  const handleDragEnd = useCallback(
    (e: React.MouseEvent) => {
      if (!dragState) return;

      const dragDuration = Date.now() - dragState.startTime;
      const wasDragging = dragDuration > 200 || hasMovedRef.current;

      if (wasDragging) {
        e.preventDefault();
        const clickEvent = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          document.removeEventListener('click', clickEvent, true);
        };
        document.addEventListener('click', clickEvent, true);
      }

      setDragState(null);
      setIsAutoScrollPaused(false);
    },
    [dragState],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let animationFrameId: number;
    let lastTimestamp: number;

    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const delta = timestamp - lastTimestamp;

      if (!isAutoScrollPaused && isVisible) {
        const maxScroll = container.scrollWidth - container.clientWidth;

        if (autoScrollPositionRef.current >= maxScroll) {
          lastTimestamp = timestamp;
          animationFrameId = requestAnimationFrame(animate);
          return;
        }

        autoScrollPositionRef.current += (speed * delta) / 1000;
        autoScrollPositionRef.current = Math.min(
          autoScrollPositionRef.current,
          maxScroll,
        );
        container.scrollLeft = autoScrollPositionRef.current;
      } else {
        autoScrollPositionRef.current = container.scrollLeft;
      }

      lastTimestamp = timestamp;
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [speed, isAutoScrollPaused, isVisible]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex flex-row gap-4 overflow-x-auto scrollbar-hide max-w-[calc(100vw-44rem)]',
        className,
        dragState ? 'cursor-grabbing' : 'cursor-grab',
      )}
      onMouseEnter={() => setIsAutoScrollPaused(true)}
      onMouseLeave={() => {
        setIsAutoScrollPaused(false);
        setDragState(null);
      }}
      onMouseDown={handleDragStart}
      onMouseUp={handleDragEnd}
      onMouseMove={handleDragMove}
      onWheel={debouncedWheelHandler}
    >
      {children}
    </div>
  );
};

export default AutoScrollingContainer;
